import { useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useSession, signOut } from "../lib/auth-client.ts";
import { ToastProvider } from "../components/Toast.tsx";

interface NavEntry {
  label: string;
  to: string;
  end?: boolean;
  icon: ReactNode;
}

const NAV: NavEntry[] = [
  {
    label: "Data",
    to: "/",
    end: true,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
        <path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3" />
      </svg>
    ),
  },
  {
    label: "Organizations",
    to: "/organizations",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 21h18" />
        <path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" />
        <path d="M9 8h1M9 12h1M9 16h1M14 8h1M14 12h1M14 16h1" />
      </svg>
    ),
  },
  {
    label: "Users",
    to: "/users",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    label: "Emails",
    to: "/emails",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
      </svg>
    ),
  },
  {
    label: "Leads",
    to: "/leads",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <line x1="19" y1="8" x2="19" y2="14" />
        <line x1="22" y1="11" x2="16" y2="11" />
      </svg>
    ),
  },
  {
    label: "Usage",
    to: "/usage",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 3v18h18" />
        <path d="M7 16l4-4 4 4 5-5" />
      </svg>
    ),
  },
  {
    label: "Plans",
    to: "/plans",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0l-7.2-7.2A2 2 0 0 1 3 12V4a1 1 0 0 1 1-1h8a2 2 0 0 1 1.4.6l6.2 6.2a2 2 0 0 1 0 2.8z" />
        <circle cx="7.5" cy="7.5" r="1.2" fill="currentColor" />
      </svg>
    ),
  },
  {
    label: "Status",
    to: "/status",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </svg>
    ),
  },
  {
    label: "Playground",
    to: "/playground",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polygon points="5 3 19 12 5 21 5 3" />
      </svg>
    ),
  },
  {
    label: "AI Queries",
    to: "/ai-queries",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
  },
];

function titleFor(path: string): string {
  if (path === "/") return "Data management";
  if (path.startsWith("/organizations")) return "Organizations";
  if (path.startsWith("/users")) return "Users";
  if (path.startsWith("/emails")) return "Email users";
  if (path.startsWith("/leads")) return "Leads & Outreach";
  if (path.startsWith("/usage")) return "Usage analytics";
  if (path.startsWith("/plans")) return "Plans";
  if (path.startsWith("/status")) return "Status & incidents";
  if (path.startsWith("/playground")) return "AI Playground";
  if (path.startsWith("/ai-queries")) return "AI Query Log";
  return "Admin";
}

export function AdminLayout() {
  const { data: session } = useSession();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const userName = session?.user.name ?? "Admin";
  const userEmail = session?.user.email ?? "";
  const initials = userName?.[0]?.toUpperCase() ?? "A";
  const title = titleFor(location.pathname);

  async function handleSignOut() {
    await signOut();
    window.location.href = "/login";
  }

  return (
    <div className="layout">
      <aside className={`sidebar${sidebarOpen ? " open" : ""}`}>
        <div className="brand">
          <img className="brand-logo" src="/logo.webp" alt="" width="26" height="26" />
          <span className="brand-name">CurrencyCore</span>
          <span className="badge badge-accent admin-tag">Admin</span>
        </div>

        <div className="nav-label">Platform</div>
        <nav className="nav">
          {NAV.map((item) => (
            <NavLink
              key={item.label}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}
              onClick={() => setSidebarOpen(false)}
            >
              {item.icon}
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-spacer" />

        <div className="user-card" title={userEmail}>
          <span className="avatar">{initials}</span>
          <span className="user-meta">
            <span className="name">{userName}</span>
            <span className="email">{userEmail}</span>
          </span>
        </div>
        <button className="btn btn-ghost btn-sm signout-btn" onClick={handleSignOut}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <path d="M16 17l5-5-5-5M21 12H9" />
          </svg>
          Sign out
        </button>
      </aside>

      {sidebarOpen && (
        <div className="scrim-mobile show" onClick={() => setSidebarOpen(false)} aria-hidden="true" />
      )}

      <div className="main">
        <header className="topbar">
          <button
            className="btn btn-icon btn-ghost hamburger"
            aria-label="Open menu"
            onClick={() => setSidebarOpen((v) => !v)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <div className="crumb">
            <span className="here">{title}</span>
          </div>
          <div className="topbar-right">
            <span className="env-pill">
              <span className="env-dot" /> Production
            </span>
          </div>
        </header>

        <div className="content">
          <div className="content-inner">
            <Outlet />
          </div>
        </div>
      </div>

      <ToastProvider />
    </div>
  );
}
